import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AppointmentActionInput,
  AppointmentBookingResource,
  AvailableSlotResource,
  CollectionMeta,
  ListAppointmentsQuery,
  RescheduleAppointmentInput,
} from '@neuroedge/shared';
import { apiClient, ApiClientError } from './client';

const KEY = ['appointments'] as const;

export interface AppointmentListPage {
  items: AppointmentBookingResource[];
  meta: CollectionMeta;
}

function toQuery(q: ListAppointmentsQuery): Record<string, string | number | undefined> {
  return {
    page: q.page,
    pageSize: q.pageSize,
    status: q.status,
    productServiceId: q.productServiceId,
    from: q.from,
    to: q.to,
    sortOrder: q.sortOrder,
  };
}

export async function listAppointments(
  q: ListAppointmentsQuery = {},
): Promise<AppointmentListPage> {
  const res = await apiClient.getCollection<AppointmentBookingResource>('/appointments', {
    query: toQuery(q),
  });
  return { items: res.data, meta: res.meta };
}
export const cancelAppointment = (id: string, input: AppointmentActionInput = {}) =>
  apiClient.post<AppointmentBookingResource>(`/appointments/${id}/cancel`, input);
export const completeAppointment = (id: string, input: AppointmentActionInput = {}) =>
  apiClient.post<AppointmentBookingResource>(`/appointments/${id}/complete`, input);
export const getAvailableSlots = (id: string) =>
  apiClient.get<AvailableSlotResource[]>(`/appointments/${id}/available-slots`);
export const rescheduleAppointment = (id: string, input: RescheduleAppointmentInput) =>
  apiClient.post<AppointmentBookingResource>(`/appointments/${id}/reschedule`, input);

export function useAppointments(query: ListAppointmentsQuery = {}) {
  return useQuery<AppointmentListPage, ApiClientError>({
    queryKey: [...KEY, 'list', query],
    queryFn: () => listAppointments(query),
    placeholderData: keepPreviousData,
  });
}

export function useCancelAppointment() {
  const qc = useQueryClient();
  return useMutation<AppointmentBookingResource, ApiClientError, { id: string; notify?: boolean }>({
    mutationFn: ({ id, notify }) => cancelAppointment(id, { notify }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useCompleteAppointment() {
  const qc = useQueryClient();
  return useMutation<AppointmentBookingResource, ApiClientError, { id: string; notify?: boolean }>({
    mutationFn: ({ id, notify }) => completeAppointment(id, { notify }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useAvailableSlots(id: string | undefined, enabled: boolean) {
  return useQuery<AvailableSlotResource[], ApiClientError>({
    queryKey: [...KEY, 'available-slots', id],
    queryFn: () => getAvailableSlots(id!),
    enabled: Boolean(id) && enabled,
  });
}

export function useRescheduleAppointment(id: string) {
  const qc = useQueryClient();
  return useMutation<AppointmentBookingResource, ApiClientError, RescheduleAppointmentInput>({
    mutationFn: (input) => rescheduleAppointment(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** A service that can be booked (has a booking schedule). */
export interface BookableService {
  id: string;
  name: string;
  slotMinutes: number;
  timezone: string;
}

export interface CreateAppointmentInput {
  productServiceId: string;
  contactId: string;
  /** ISO time; an open slot or any other future time. */
  scheduledStart: string;
  notes?: string;
  /** Send the customer a WhatsApp confirmation (if they have a chat with you). */
  notify?: boolean;
}

export function useBookableServices(enabled = true) {
  return useQuery<BookableService[], ApiClientError>({
    queryKey: [...KEY, 'services'],
    queryFn: () => apiClient.get<BookableService[]>('/appointments/services'),
    enabled,
  });
}

/** Open slots of a service, for a new appointment. */
export function useServiceSlots(productServiceId: string) {
  return useQuery<AvailableSlotResource[], ApiClientError>({
    queryKey: [...KEY, 'service-slots', productServiceId],
    queryFn: () =>
      apiClient.get<AvailableSlotResource[]>(
        `/appointments/services/${productServiceId}/available-slots`,
      ),
    enabled: Boolean(productServiceId),
  });
}

export function useCreateAppointment() {
  const qc = useQueryClient();
  return useMutation<AppointmentBookingResource, ApiClientError, CreateAppointmentInput>({
    mutationFn: (input) => apiClient.post<AppointmentBookingResource>('/appointments', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
